import type { RateLookupWidgetConfig } from "@/types/home";

/** Last-used Rate Lookup criteria on the rates page (session-scoped). */
export const RATE_LOOKUP_CRITERIA_KEY = "twido-v2-rate-lookup-criteria";

const STRING_FIELDS = [
  "origin_port",
  "destination_port",
  "equipment_size",
  "equipment_type",
  "valid_on",
] as const satisfies readonly (keyof RateLookupWidgetConfig)[];

function readString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function readCargoKg(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }
  return value;
}

function readLoadType(value: unknown): "fcl" | "lcl" | undefined {
  if (typeof value !== "string") return undefined;
  const lt = value.trim().toLowerCase();
  return lt === "fcl" || lt === "lcl" ? lt : undefined;
}

/** Session wins over saved home-layout defaults for each field. */
export function mergeRateLookupConfig(
  layout: RateLookupWidgetConfig = {},
  session: RateLookupWidgetConfig = {},
): RateLookupWidgetConfig {
  return sanitizeRateLookupCriteria({ ...layout, ...session });
}

/** Keep only known Rate Lookup fields; drop empty / invalid values. */
export function sanitizeRateLookupCriteria(
  input: unknown,
): RateLookupWidgetConfig {
  if (!input || typeof input !== "object") return {};
  const raw = input as Record<string, unknown>;
  const next: RateLookupWidgetConfig = {};
  for (const key of STRING_FIELDS) {
    const value = readString(raw[key]);
    if (value) next[key] = value;
  }
  const cargoKg = readCargoKg(raw.cargo_kg);
  if (cargoKg != null) next.cargo_kg = cargoKg;
  const loadType = readLoadType(raw.load_type);
  if (loadType != null) next.load_type = loadType;
  return next;
}

export function readRateLookupCriteria(): RateLookupWidgetConfig {
  if (typeof sessionStorage === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(RATE_LOOKUP_CRITERIA_KEY);
    if (!raw) return {};
    return sanitizeRateLookupCriteria(JSON.parse(raw));
  } catch {
    return {};
  }
}

export function writeRateLookupCriteria(
  config: RateLookupWidgetConfig,
): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(
    RATE_LOOKUP_CRITERIA_KEY,
    JSON.stringify(sanitizeRateLookupCriteria(config)),
  );
}
